import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import { useTable, usePagination, useSortBy, useGlobalFilter } from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEye, faEdit, faPlus, faCheck, faPencil, faCheckCircle, faLock } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/* ---------------- Search Component ---------------- */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    className="form-control"
    placeholder="Search..."
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value)}
  />
);

/* ---------------- Table Component ---------------- */
const TableComponent = ({ columns, data, globalFilter, setGlobalFilter, pageSize, setPageSize }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
    setGlobalFilter: tableSetFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize, globalFilter },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Sync input → table filter
  React.useEffect(() => {
    tableSetFilter(globalFilter);
  }, [globalFilter]);

  const { pageIndex } = state;

  return (
    <>
      <table className="table table-striped" {...getTableProps()}>
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render("Header")}{" "}
                  {col.isSorted ? (col.isSortedDesc ? "🔽" : "🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);

            return (
              <tr
                {...row.getRowProps()}

              >
                {row.cells.map((cell) => (
                  <td style={{
                    color:
                      row.original.status === "Pending"
                        ? "#0d6efd"
                        : row.original.status === "Approved"
                          ? "#198754"
                          : "inherit",
                  }} {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center">
        <button className="btn btn-secondary" disabled={!canPreviousPage} onClick={previousPage}>
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>

        <button className="btn btn-secondary" disabled={!canNextPage} onClick={nextPage}>
          Next
        </button>
      </div>
    </>
  );
};

/* ---------------- Main Component ---------------- */
const ProjectRequirementList = ({ accessOfDepartments, permissions }) => {
  const navigate = useNavigate();
  const code = localStorage.getItem("code");

  const [tableData, setTableData] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(20);

  /* ------------ Fetch Data ------------ */
  const loadData = async () => {
    setLoading(true);
    try {
      if (statusFilter === "pending") {
        const { data } = await apiClient.get(SERVER_BASE + "/getPendingProjectRequirement", {
          params: { code, accessOfDepartments },
        });
        setTableData(data);
      } else if (statusFilter === "approved") {
        const { data } = await apiClient.get(SERVER_BASE + "/getApprovedProjectRequirement", {
          params: { code, accessOfDepartments },
        });
        setTableData(data);
      } else {
        const [p, a] = await Promise.all([
          apiClient.get(SERVER_BASE + "/getPendingProjectRequirement", {
            params: { code, accessOfDepartments },
          }),
          apiClient.get(SERVER_BASE + "/getApprovedProjectRequirement", {
            params: { code, accessOfDepartments },
          }),
        ]);
        setTableData([...p.data, ...a.data]);
      }
    } catch (error) {
      console.error("Error loading requirements:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  /* ------------ Status Dropdown ------------ */
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "all", label: "All" },
  ];

  /* ------------ Columns ------------ */
  const columns = useMemo(
    () => [
      { Header: "S.No", Cell: ({ row }) => row.index + 1 },

      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => moment(value).format("DD-MMM-YY"),
      },

      { Header: "Ref No.", accessor: "requirementCode" },
      { Header: "Department", accessor: "department" },
      { Header: "Project", accessor: "projectName" },
      { Header: "Status", accessor: "status" },

      {
        Header: "From",
        accessor: "from",
        Cell: ({ row }) => {
          const d = row.original;
          const zonal = Number(d.zonalCount || 0);
          const local = Number(d.localCount || 0);

          if (zonal > 0 && local > 0) return "Local + Zonal";
          if (zonal > 0) return "Zonal";
          if (local > 0) return "Local";
          return "-";
        },
      },
      {
        Header: 'Preview', accessor: 'Preview', Cell: ({ row }) => {
          const d = row.original
          return (
            <div className="d-flex gap-1">
            {permissions?.read &&
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() =>
                navigate("../../ProjectRequirementPreview", { state: { id: d.id } })
              }
            >
              View
            </button>}
                            {permissions?.edit && (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => navigate("../ProjectRequirement", { state: { id: d.id } })}
                              >
                                <FontAwesomeIcon icon={faLock} className="" />
            
                              </button>
                            )}
                            </div>
          )
        }
      },

      {
        Header: "#",
        Cell: ({ row }) => {
          const d = row.original;

          return (
            <div className="dropdown">
              <button className="btn btn-light" data-bs-toggle="dropdown">
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                {/* Edit when Pending */}
                {/* {permissions?.edit && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => navigate("../ProjectRequirement", { state: { id: d.id } })}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                  </li>
                )} */}

                {/* Preview
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() =>
                      navigate("../../ProjectRequirementPreview", { state: { id: d.id } })
                    }
                  >
                    <FontAwesomeIcon icon={faEye} /> Preview
                  </button>
                </li> */}

                <li>
                  <hr />
                </li>
                <li>
                  {d.createdByName && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPlus} /> {d.createdByName}
                      {d.createdAt &&
                        ` (${moment(d.createdAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {d.updatedByName && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPencil} /> {d.updatedByName}
                      {d.updatedAt &&
                        ` (${moment(d.updatedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {d.approvedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faCheckCircle} /> {d.approvedBy}
                      {d.approvedAt &&
                        ` (${moment(d.approvedAt).format(
                          "DD-MMM-YY hh:mm A"
                        )})`}
                    </li>
                  )}
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  return (
    <div className="formcontainer mt-5">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4>Project Requirement</h4>
        <div className="d-flex gap-3">

          <button
            className="btn btn-outline-primary"
            onClick={() =>
              navigate("../../ConsolidatedReport", {
                state: { accessOfDepartments }
              })
            }
          >
            Get Report
          </button>
        </div>
      </div>

      {/* ---------------- TOP BAR FILTERS ---------------- */}
      <div className="row align-items-center mb-3">
        {/* Add Button */}
        <div className="col-md-2 mb-2">
          {permissions?.write && (
            <button
              className="btn btn-outline-success w-100"
              onClick={() => navigate("../ProjectRequirement")}
            >
              Add
            </button>
          )}
        </div>

        {/* Search */}
        <div className="col-md-5 mb-2">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>

        {/* Status Filter */}
        <div className="col-md-3 mb-2">
          <Select
            options={statusOptions}
            value={statusOptions.find((s) => s.value === statusFilter)}
            onChange={(v) => setStatusFilter(v.value)}
          />
        </div>

        {/* Page Size */}
        <div className="col-md-2 mb-2">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[20, 30, 50, 100].map((s) => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ---------------- TABLE ---------------- */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableComponent
          columns={columns}
          data={tableData}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

export default ProjectRequirementList;
